import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";

/**
 * Must stay identical to the `@media` condition in `styles/navigation/breadcrumbs.css`
 * (narrow slimline breadcrumb rules). Uses the same upper bound as Bootstrap `md` (768px).
 */
export const narrowBreadcrumbMediaQuery = "(max-width: 767.98px)";

function useNarrowBreadcrumbLayout(): boolean {
    const [narrow, setNarrow] = useState(
        () => typeof window !== "undefined" && window.matchMedia(narrowBreadcrumbMediaQuery).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(narrowBreadcrumbMediaQuery);
        const sync = () => setNarrow(mq.matches);
        sync();
        mq.addEventListener("change", sync);
        return () => mq.removeEventListener("change", sync);
    }, []);

    return narrow;
}

export type AppBreadcrumbItem = {
    text: string;
    to?: string;
    href?: string;
    labelNode?: React.ReactNode;
};

function renderLabel(item: AppBreadcrumbItem): React.ReactNode {
    return item.labelNode ?? item.text;
}

function getMobileBackTarget(items: AppBreadcrumbItem[]): AppBreadcrumbItem | undefined {
    if (items.length < 2) {
        return undefined;
    }
    for (let i = items.length - 2; i >= 0; i--) {
        const it = items[i];
        if (it.to || it.href) {
            return it;
        }
    }
    return undefined;
}

function isInternalPath(href: string): boolean {
    return href.startsWith("/") && !href.startsWith("//");
}

export type ResponsiveBreadcrumbProps = {
    items: AppBreadcrumbItem[];
    className?: string;
};

/**
 * Narrow layout: slim "Back to …" link (see `narrowBreadcrumbMediaQuery` + breadcrumbs.css).
 * Wider: standard pill-style breadcrumb trail.
 */
export const ResponsiveBreadcrumb: React.FC<ResponsiveBreadcrumbProps> = ({ items, className }) => {
    const isNarrow = useNarrowBreadcrumbLayout();

    if (items.length === 0) {
        return null;
    }

    if (isNarrow) {
        const back = getMobileBackTarget(items);
        if (!back) {
            return null;
        }

        const backLinkClass = "breadcrumb-narrow-back-link";

        const backBody = (
            <>
                <span className="breadcrumb-narrow-back-chevron" aria-hidden>
                    ←
                </span>
                <span>Back to {back.text}</span>
            </>
        );

        return (
            <nav className={`breadcrumb-narrow ${className ?? ""}`.trim()} aria-label="Breadcrumb">
                <div className="breadcrumb-narrow-back">
                    {back.to ? (
                        <Link to={back.to} className={backLinkClass}>
                            {backBody}
                        </Link>
                    ) : back.href && isInternalPath(back.href) ? (
                        <Link to={back.href} className={backLinkClass}>
                            {backBody}
                        </Link>
                    ) : back.href ? (
                        <a href={back.href} className={backLinkClass}>
                            {backBody}
                        </a>
                    ) : null}
                </div>
            </nav>
        );
    }

    return (
        <Breadcrumb className={className}>
            {items.map((item, index) => {
                const isLast = index === items.length - 1;
                const label = renderLabel(item);

                if (isLast) {
                    return (
                        <Breadcrumb.Item active key={`${item.text}-${index}`}>
                            {label}
                        </Breadcrumb.Item>
                    );
                }

                if (item.to) {
                    return (
                        <Breadcrumb.Item key={`${item.text}-${index}`} linkAs={Link} linkProps={{ to: item.to }}>
                            {label}
                        </Breadcrumb.Item>
                    );
                }

                if (item.href) {
                    if (isInternalPath(item.href)) {
                        return (
                            <Breadcrumb.Item key={`${item.text}-${index}`} linkAs={Link} linkProps={{ to: item.href }}>
                                {label}
                            </Breadcrumb.Item>
                        );
                    }
                    return (
                        <Breadcrumb.Item key={`${item.text}-${index}`} href={item.href}>
                            {label}
                        </Breadcrumb.Item>
                    );
                }

                return (
                    <Breadcrumb.Item key={`${item.text}-${index}`} active>
                        {label}
                    </Breadcrumb.Item>
                );
            })}
        </Breadcrumb>
    );
};
